import React, { useState, useEffect, useRef } from 'react';
import { Box, chakra, shouldForwardProp, usePrefersReducedMotion } from '@chakra-ui/react';
import { motion, useAnimation, isValidMotionProp } from 'framer-motion';
import SanityImage from 'gatsby-plugin-sanity-image';

const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) => isValidMotionProp(prop) || shouldForwardProp(prop),
});

const HeroImage = ({ image }) => {
  const [scrollY, setScrollY] = useState(0);
  const controls = useAnimation();
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Use Chakra UI's hook to check for reduced motion preference
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleScroll = () => {
    if (!prefersReducedMotion) {
      setScrollY(window.scrollY);
    }
  };

  useEffect(() => {
    if (!prefersReducedMotion) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!prefersReducedMotion) {
      const updateAnimation = () => {
        const containerHeight = containerRef.current.clientHeight;
        const imageHeight = imageRef.current.clientHeight;
        const maxOffset = imageHeight - containerHeight;
        const yOffset = Math.min(scrollY * 0.2, maxOffset); // Ensure yOffset does not exceed maxOffset

        controls.start({
          y: -yOffset,
          transition: { type: 'spring', stiffness: 50, damping: 20 },
        });
      };

      updateAnimation(); // Update animation initially
      window.addEventListener('scroll', updateAnimation); // Update on scroll

      return () => window.removeEventListener('scroll', updateAnimation);
    }
  }, [scrollY, controls, prefersReducedMotion]);

  return (
    <Box
      ref={containerRef}
      position='relative'
      width='100%'
      height={{ base: '300px', md: '600px' }}
      overflow='hidden'
      backgroundColor='gray.100'
      as='figure'
    >
      <MotionBox
        position='absolute'
        width='100%'
        height={{ base: '500px', md: '800px' }}
        // Only apply animate prop when reduced motion is not preferred
        animate={prefersReducedMotion ? undefined : controls}
        ref={imageRef}
        display='flex'
      >
        <SanityImage
          {...image}
          alt={image?.asset?.altText}
          width={900}
          hotspot={image?.hotspot}
          crop={image?.crop}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </MotionBox>
      {image?.asset?.title && (
        <Box
          position='absolute'
          bottom={20}
          right={20}
          maxWidth='90%'
          fontWeight={600}
          color='white'
          as='figcaption'
          textShadow='2px 2px 12px rgba(0, 0, 0, 0.9), 0 0 5px rgba(0, 0, 0, 0.7)'
          backgroundColor='rgba(0, 0, 0, 0.7)'
          padding='8px 12px'
          borderRadius='8px'
        >
          {image.asset.title}
        </Box>
      )}
    </Box>
  );
};

export default HeroImage;
